import type { ReactNode } from 'react'
import { Icon } from '@/shared/components/Icon'
import { useT } from '@/shared/i18n'

// ---------------------------------------------------------------------------
// Topbar — presentational global topbar chrome.
//
// Renders the full-width topbar bar (grid-column: 1/-1 in the .app grid).
// All content is slot-driven: brand, search, actions, user. The only piece of
// opinionated rendering is an optional "open display settings" icon button
// injected into the actions area when onOpenDisplaySettings is provided.
//
// Props:
//   brand                  — brand slot (logo + name element)
//   search?                — search slot, rendered as-is between brand and the
//                            right-hand actions. The slot owns its own wrapper
//                            (e.g. CommandPalette renders the .topbar__search
//                            region itself), so the Topbar adds no extra box —
//                            this keeps the .search-pop's relative anchor and
//                            the search landmark inside the slot.
//   actions?               — icon-button slot on the right (notifications, etc.)
//   user?                  — user chip / avatar slot at the far right
//   onOpenDisplaySettings? — when provided, renders a "display settings" gear
//                            button that calls this handler, wrapped together
//                            with the panel slot in a .popover-wrap anchor
//   displaySettingsPanel?  — the open display-settings popover element. Rendered
//                            inside the .popover-wrap next to the gear so the
//                            panel's absolute positioning anchors to the button
//                            (not the viewport). Parent owns open/close state.
//   onMenuClick?           — when provided, renders a leading hamburger button
//                            (.topbar__menu-btn) before the brand that calls this
//                            handler. The button is hidden at desktop width and
//                            only appears at the mobile breakpoint via CSS, so a
//                            surface that has no off-canvas drawer can leave it
//                            unset and the topbar is byte-identical.
//   menuButtonLabel?       — localised aria-label for the hamburger button. The
//                            label is surface-supplied so Topbar stays i18n-free
//                            for the menu affordance.
//
// Renders a <header> landmark so the topbar is a11y-navigable.
// ---------------------------------------------------------------------------

export type TopbarProps = {
  brand: ReactNode
  search?: ReactNode
  actions?: ReactNode
  user?: ReactNode
  onOpenDisplaySettings?: () => void
  displaySettingsPanel?: ReactNode
  /** Overrides the display-settings button label on a surface whose panel
   *  offers fewer controls than the default label names. */
  displaySettingsLabel?: string
  onMenuClick?: () => void
  menuButtonLabel?: string
}

/**
 * Presentational topbar shell primitive.
 *
 * - The brand slot fills the space aligned with the sidebar width.
 * - The search slot (if provided) stretches to fill available horizontal space.
 * - The actions slot and the optional display-settings button live in .topbar__right.
 * - The user slot is the rightmost element.
 *
 * Usage:
 *   <Topbar
 *     brand={<Link href="/">Mesterbrev</Link>}
 *     search={<GlobalSearch />}
 *     actions={<NotificationButton count={3} />}
 *     onOpenDisplaySettings={() => openPanel()}
 *     user={<UserMenu />}
 *   />
 */
export function Topbar({
  brand,
  search,
  actions,
  user,
  onOpenDisplaySettings,
  displaySettingsPanel,
  displaySettingsLabel,
  onMenuClick,
  menuButtonLabel,
}: TopbarProps) {
  const t = useT()

  return (
    <header className="topbar">
      {onMenuClick != null && (
        <button
          type="button"
          className="topbar__menu-btn"
          aria-label={menuButtonLabel ?? t('chrome.sidebar.expand')}
          onClick={onMenuClick}
        >
          <Icon name="list" size={18} aria-hidden="true" />
        </button>
      )}

      <div className="topbar__brand">{brand}</div>

      {search}

      <div className="topbar__right">
        {actions}

        {onOpenDisplaySettings != null && (
          <div className="popover-wrap">
            <button
              type="button"
              className="topbar__btn"
              aria-label={displaySettingsLabel ?? t('chrome.topbar.display_settings')}
              aria-haspopup="dialog"
              aria-expanded={Boolean(displaySettingsPanel)}
              onClick={onOpenDisplaySettings}
            >
              <Icon name="settings" size={18} aria-hidden="true" />
            </button>
            {displaySettingsPanel}
          </div>
        )}

        {user}
      </div>
    </header>
  )
}
